/**
 *  @class
 *  @function FacetsToggle
 */
class FacetsToggle {
  constructor() {
    this.container = document.getElementById('Facet-Drawer');
    let button = document.getElementById('Facets-Toggle');

    if (button && this.container) {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        document.body.classList.toggle('open-cc');
        this.container.classList.toggle('active');
      });
    }

    setTimeout(() => {
      window.dispatchEvent(new Event('resize.resize-select'));
    }, 10);
  }
}

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 250);

    const form = this.querySelector('form');
    if (form) {
      form.addEventListener('input', this.debouncedOnSubmit.bind(this));
    }

    FacetFiltersForm.refreshSubcategoryRefMaps();
    if (!FacetFiltersForm.initialized) {
      const initialParams = new URLSearchParams(window.location.search.slice(1));
      const directCategoryContext = FacetFiltersForm.getDirectCategoryContext();
      if (directCategoryContext) {
        const categoryParam = FacetFiltersForm.defaultSubcategoryRefFilterParam;
        if (!initialParams.getAll(categoryParam).includes(directCategoryContext.currentRef)) {
          initialParams.append(categoryParam, directCategoryContext.currentRef);
        }
        FacetFiltersForm.directCategoryContext = directCategoryContext;
      }
      const normalizedInitial = FacetFiltersForm.normalizeSearchParams(initialParams);
      FacetFiltersForm.searchParamsInitial = normalizedInitial;
      FacetFiltersForm.searchParamsPrev = normalizedInitial;
      FacetFiltersForm.requestPath = directCategoryContext
        ? directCategoryContext.requestPath
        : window.location.pathname;
      FacetFiltersForm.initialized = true;

      history.replaceState(
        { searchParams: normalizedInitial, requestPath: FacetFiltersForm.requestPath },
        '',
        FacetFiltersForm.getVisibleFacetUrl(normalizedInitial)
      );

      FacetFiltersForm.initializeInPlaceCategoryRouting();
    }

    FacetFiltersForm.captureFacetRowTemplates();
    FacetFiltersForm.bindFacetAvailabilityListeners();
    FacetFiltersForm.restoreCategoryFacetOpen();
    FacetFiltersForm.bindCollectionResetLinks();
    FacetFiltersForm.hydrateDirectCategoryFacets();
    FacetFiltersForm.syncScopedCategoryBreadcrumb();
  }

  static setListeners() {
    window.addEventListener('popstate', (event) => {
      const searchParams = event.state
        ? event.state.searchParams
        : FacetFiltersForm.normalizeSearchParams(window.location.search.slice(1));

      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    });
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((el) => {
      el.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    const normalizedSearchParams = FacetFiltersForm.normalizeSearchParams(searchParams);
    const categoryParam = FacetFiltersForm.defaultSubcategoryRefFilterParam;
    const changedCategoryControl = event?.target?.matches?.(
      `input[type="checkbox"][name="${categoryParam}"]`
    );

    if (changedCategoryControl) {
      FacetFiltersForm.keepCategoryFacetOpen = true;
      FacetFiltersForm.setCategoryFacetOpen(true);
    }

    if (updateURLHash) {
      const visibleUrl = FacetFiltersForm.getVisibleFacetUrl(normalizedSearchParams);
      const visibleLocation = new URL(visibleUrl, window.location.origin);
      if (visibleLocation.pathname !== window.location.pathname) {
        if (!FacetFiltersForm.canRenderCategoryRouteInPlace(visibleLocation)) {
          if (changedCategoryControl) FacetFiltersForm.rememberCategoryFacetOpen();
          window.location.assign(visibleUrl);
          return;
        }
      }
    }

    FacetFiltersForm.searchParamsPrev = normalizedSearchParams;
    const renderGeneration = ++FacetFiltersForm.renderGeneration;
    const sections = FacetFiltersForm.getSections();
    FacetFiltersForm.lockScrollAnchoring();

    document
      .getElementById('ProductGridContainer')
      ?.querySelector('.collection')
      ?.classList.add('loading');

    document.querySelectorAll('.thb-filter-count').forEach(el => {
      el.classList.add('loading');
    });

    sections.forEach((section) => {
      const url = `${FacetFiltersForm.requestPath}?section_id=${section.section}&${normalizedSearchParams}`;
      const cached = FacetFiltersForm.filterData.find(d => d.url === url);

      if (cached) {
        FacetFiltersForm.renderSectionFromCache(cached, event, renderGeneration);
      } else {
        FacetFiltersForm.renderSectionFromFetch(url, event, renderGeneration);
      }
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(normalizedSearchParams);
  }

  static renderSectionFromFetch(url, event, renderGeneration) {
    FacetFiltersForm.fetchSectionHTML(url)
      .then(async html => {
        FacetFiltersForm.filterData.push({ html, url });

        const filtersRendered = await FacetFiltersForm.renderFilters(
          html,
          event,
          renderGeneration
        );
        if (!filtersRendered) return;
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);

        FacetFiltersForm.bindFacetAvailabilityListeners();

        new FacetsToggle();
        FacetFiltersForm.releaseScrollAnchoring(renderGeneration);
      })
      .catch(() => {
        document
          .getElementById('ProductGridContainer')
          ?.querySelector('.collection')
          ?.classList.remove('loading');
        document.querySelectorAll('.thb-filter-count').forEach(el => {
          el.classList.remove('loading');
        });
        FacetFiltersForm.releaseScrollAnchoring(renderGeneration);
      });
  }

  static async fetchSectionHTML(url, attempts = 3) {
    for (let attempt = 0; attempt < attempts; attempt += 1) {
      let response;
      try {
        response = await fetch(url, { credentials: 'same-origin' });
        if (response.ok) return response.text();
      } catch (error) {
        if (attempt + 1 >= attempts) throw error;
      }

      if (attempt + 1 < attempts) {
        const retryAfterSeconds = Number(response?.headers.get('Retry-After'));
        const retryDelay =
          response?.status === 429
            ? (Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0
                ? retryAfterSeconds * 1000
                : 2000 * (attempt + 1))
            : 500 * (attempt + 1);
        await new Promise(resolve =>
          window.setTimeout(resolve, retryDelay)
        );
      }
    }

    throw new Error('Unable to refresh collection filters');
  }

  static async renderSectionFromCache(data, event, renderGeneration) {
    const html = data.html;

    const filtersRendered = await FacetFiltersForm.renderFilters(
      html,
      event,
      renderGeneration
    );
    if (!filtersRendered) return;
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);

    FacetFiltersForm.bindFacetAvailabilityListeners();

    new FacetsToggle();
    FacetFiltersForm.releaseScrollAnchoring(renderGeneration);
  }

  static lockScrollAnchoring() {
    if (!FacetFiltersForm.scrollAnchorStyles) {
      FacetFiltersForm.scrollAnchorStyles = {
        root: document.documentElement.style.overflowAnchor,
        body: document.body.style.overflowAnchor
      };
    }

    document.documentElement.style.overflowAnchor = 'none';
    document.body.style.overflowAnchor = 'none';
  }

  static releaseScrollAnchoring(renderGeneration) {
    if (renderGeneration !== FacetFiltersForm.renderGeneration) return;

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        if (renderGeneration !== FacetFiltersForm.renderGeneration) return;

        const styles = FacetFiltersForm.scrollAnchorStyles;
        if (!styles) return;
        document.documentElement.style.overflowAnchor = styles.root;
        document.body.style.overflowAnchor = styles.body;
        FacetFiltersForm.scrollAnchorStyles = null;
      });
    });
  }

  static renderProductGridContainer(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const newGrid = doc.getElementById('ProductGridContainer');
    const current = document.getElementById('ProductGridContainer');

    if (!newGrid || !current) return;
    const currentSidebar = current.querySelector('.sidebar-container');
    const newSidebar = newGrid.querySelector('.sidebar-container');
    const currentResults = currentSidebar?.lastElementChild;
    const newResults = newSidebar?.lastElementChild;

    if (!currentResults) return;
    if (newResults) {
      currentResults.replaceWith(newResults);
      return;
    }

    const newEmptyState = newGrid.querySelector(':scope > .collection-empty');
    if (newEmptyState) currentResults.replaceWith(newEmptyState);
  }

  static renderProductCount(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const countHtml = doc.getElementById('ProductCount');

    if (!countHtml) return;

    document.querySelectorAll('.thb-filter-count').forEach(el => {
      el.innerHTML = countHtml.innerHTML;
      el.classList.remove('loading');
    });
  }

  static async renderFilters(html, event, renderGeneration) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const interactedRow = event?.target?.closest?.('collapsible-row');
    const hasActiveFilters = Array.from(
      new URLSearchParams(FacetFiltersForm.searchParamsPrev).keys()
    ).some(key => key.startsWith('filter.'));

    if (!hasActiveFilters) {
      FacetFiltersForm.captureFacetRowTemplatesFromRoot(parsedHTML, true);
    }

    const rowUpdates = [];
    ['#FacetFiltersForm', '#FacetFiltersFormMobile'].forEach(formSelector => {
      const sourceForm = parsedHTML.querySelector(formSelector);
      const targetForm = document.querySelector(formSelector);
      if (!targetForm) return;

      const sourceRows = sourceForm
        ? Array.from(sourceForm.querySelectorAll('collapsible-row'))
        : [];
      targetForm.querySelectorAll('collapsible-row').forEach(target => {
        const template = FacetFiltersForm.getFacetRowTemplate(
          formSelector,
          target.dataset.filterParam
        );
        const source = sourceRows.find(
          row => row.dataset.filterParam === target.dataset.filterParam
        );

        if (target === interactedRow) {
          rowUpdates.push({ target, interacted: true });
          return;
        }

        // Shopify already prunes narrow collections on the server. Keep that
        // refreshed row so brand collections do not regain options from the
        // original, unfiltered template after a Category selection.
        const useServerPrunedSource =
          target.hasAttribute('data-inventory-pruned-facet') ||
          source?.hasAttribute('data-inventory-pruned-facet');
        const updateSource = useServerPrunedSource ? source : template || source;
        rowUpdates.push({
          target,
          prepared: updateSource
            ? FacetFiltersForm.prepareFacetRowForRefresh(updateSource)
            : Promise.resolve(null)
        });
      });
    });

    const preparedUpdates = await Promise.all(
      rowUpdates.map(async update => ({
        ...update,
        prepared: update.prepared ? await update.prepared : null
      }))
    );
    if (renderGeneration !== FacetFiltersForm.renderGeneration) return false;

    preparedUpdates.forEach(({ target, interacted, prepared }) => {
      if (!target.isConnected) return;
      if (interacted) {
        FacetFiltersForm.syncListFacetSelectionFromSearchParams(target);
        FacetFiltersForm.closeInactiveRange(target);
        return;
      }
      if (!prepared) {
        target.remove();
        return;
      }

      target.dataset.inventoryAwareGeneration = String(
        Number(target.dataset.inventoryAwareGeneration || 0) + 1
      );
      FacetFiltersForm.refreshFacetRowContent(target, prepared);
    });

    if (FacetFiltersForm.keepCategoryFacetOpen) {
      FacetFiltersForm.setCategoryFacetOpen(true);
      FacetFiltersForm.keepCategoryFacetOpen = false;
    }

    FacetFiltersForm.refreshSubcategoryRefMaps();
    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);
    return true;
  }

  static async prepareFacetRowForRefresh(source) {
    const prepared = source.cloneNode(true);
    FacetFiltersForm.syncListFacetSelectionFromSearchParams(prepared);
    FacetFiltersForm.closeInactiveRange(prepared);
    if (prepared.hasAttribute('data-inventory-aware-facet')) {
      await FacetFiltersForm.resolveFacetAvailability(prepared);
    }
    return prepared;
  }

  static closeInactiveRange(row) {
    if (row.querySelector('input[type="checkbox"][name]')) return;
    const filterParam = row.dataset.filterParam;
    const activeParams = new URLSearchParams(FacetFiltersForm.searchParamsPrev);
    const hasActiveRange =
      activeParams.has(`${filterParam}.gte`) ||
      activeParams.has(`${filterParam}.lte`);
    if (!hasActiveRange) row.querySelector('details').open = false;
  }

  static refreshFacetRowContent(target, source) {
    const targetDetails = target.querySelector('details');
    const sourceDetails = source.querySelector('details');
    const targetContent = targetDetails?.querySelector('.collapsible__content');
    const sourceContent = sourceDetails?.querySelector('.collapsible__content');
    if (!targetDetails || !sourceDetails || !targetContent || !sourceContent) return;

    const focusedControl = target.contains(document.activeElement)
      ? {
          id: document.activeElement.id,
          name: document.activeElement.name,
          value: document.activeElement.value
        }
      : null;
    const wasOpen = targetDetails.open;

    targetContent.innerHTML = sourceContent.innerHTML;
    targetDetails.open = wasOpen;

    FacetFiltersForm.facetRowPreparations.delete(target);
    delete target.dataset.inventoryAwareResolving;
    if (source.dataset.inventoryAwareResolved === 'true') {
      target.dataset.inventoryAwareResolved = 'true';
    } else {
      delete target.dataset.inventoryAwareResolved;
    }
    target.hidden = source.hidden;

    if (!focusedControl) return;
    const replacement = Array.from(target.querySelectorAll('input, select, button')).find(
      control =>
        (focusedControl.id && control.id === focusedControl.id) ||
        (focusedControl.name &&
          control.name === focusedControl.name &&
          control.value === focusedControl.value)
    );
    replacement?.focus({ preventScroll: true });
  }

  static setCategoryFacetOpen(open) {
    const categoryParam = FacetFiltersForm.defaultSubcategoryRefFilterParam;
    document
      .querySelectorAll(`collapsible-row[data-filter-param="${categoryParam}"] details`)
      .forEach(details => {
        details.open = open;
      });
  }

  static rememberCategoryFacetOpen() {
    try {
      window.sessionStorage.setItem(
        FacetFiltersForm.categoryOpenStorageKey,
        String(Date.now())
      );
    } catch (error) {
      // The facet still works when session storage is unavailable.
    }
  }

  static clearRememberedCategoryFacetOpen() {
    try {
      window.sessionStorage.removeItem(FacetFiltersForm.categoryOpenStorageKey);
    } catch (error) {
      // The reset navigation still collapses the server-rendered facet.
    }
  }

  static restoreCategoryFacetOpen() {
    let shouldRestore = false;
    try {
      const storedAt = Number(
        window.sessionStorage.getItem(FacetFiltersForm.categoryOpenStorageKey)
      );
      FacetFiltersForm.clearRememberedCategoryFacetOpen();
      shouldRestore = Number.isFinite(storedAt) && Date.now() - storedAt < 10000;
    } catch (error) {
      shouldRestore = false;
    }

    if (shouldRestore) FacetFiltersForm.setCategoryFacetOpen(true);
  }

  static bindCollectionResetLinks() {
    if (FacetFiltersForm.collectionResetLinksBound) return;
    FacetFiltersForm.collectionResetLinksBound = true;
    document.addEventListener('click', event => {
      if (!event.target.closest?.('[data-filter-reset-collection]')) return;
      FacetFiltersForm.keepCategoryFacetOpen = false;
      FacetFiltersForm.clearRememberedCategoryFacetOpen();
    });
  }

  static initializeInPlaceCategoryRouting() {
    const requestHandle = (FacetFiltersForm.requestPath || '')
      .split('/')
      .filter(Boolean)[1];
    const context = FacetFiltersForm.directCategoryContext;
    FacetFiltersForm.inPlaceCategoryRouting = Boolean(
      context || FacetFiltersForm.directCategoryOriginHandles.has(requestHandle)
    );

    if (!context) return;

    context.currentTitle = document
      .querySelector('.collection-grid-heading__title')
      ?.textContent?.trim();

    document.querySelectorAll('.breadcrumbs').forEach(nav => {
      const originLink = Array.from(nav.querySelectorAll('a')).find(link => {
        const pathname = new URL(link.href, window.location.origin).pathname;
        return pathname === context.requestPath;
      });
      if (!originLink) return;

      let sibling = originLink.nextSibling;
      while (sibling) {
        const next = sibling.nextSibling;
        sibling.remove();
        sibling = next;
      }
      originLink.dataset.filterResetCollection = '';
      nav.dataset.facetBaseTitle = originLink.textContent.trim();
    });

    document.querySelectorAll('.collection-grid-heading__title').forEach(title => {
      const baseTitle = document.querySelector('.breadcrumbs')?.dataset.facetBaseTitle;
      if (baseTitle) title.dataset.facetBaseTitle = baseTitle;
    });
  }

  static canRenderCategoryRouteInPlace(visibleLocation) {
    if (!FacetFiltersForm.inPlaceCategoryRouting) return false;

    const requestPath = FacetFiltersForm.requestPath || window.location.pathname;
    if (visibleLocation.pathname === requestPath) return true;

    const requestHandle = requestPath.split('/').filter(Boolean)[1];
    return visibleLocation.searchParams.get('subcategory_from') === requestHandle;
  }

  static renderActiveFacets(html) {
    const source = html.querySelector('.active-facets');

    if (source) {
      const categoryParam = FacetFiltersForm.defaultSubcategoryRefFilterParam;
      const selectedCategories = new URLSearchParams(
        FacetFiltersForm.searchParamsPrev
      ).getAll(categoryParam);
      const usesScopedCategoryBreadcrumb =
        selectedCategories.length === 1 &&
        FacetFiltersForm.usesScopedCategoryBreadcrumb();
      if (
        selectedCategories.length === 1 &&
        (FacetFiltersForm.directCategoryContext || usesScopedCategoryBreadcrumb)
      ) {
        source
          .querySelectorAll(`[data-filter-param="${categoryParam}"]`)
          .forEach(chip => chip.remove());

        const clearAll = source.querySelector('.active-facets__button-remove');
        if (clearAll) {
          if (
            FacetFiltersForm.directCategoryContext &&
            source.querySelector('.active-facets__button')
          ) {
            const categoryOnly = new URLSearchParams();
            categoryOnly.append(categoryParam, selectedCategories[0]);
            clearAll.href = FacetFiltersForm.getVisibleFacetUrl(categoryOnly);
          } else if (!source.querySelector('.active-facets__button')) {
            clearAll.remove();
          }
        }
      }
      document.querySelectorAll('.active-facets').forEach(target => {
        target.innerHTML = source.innerHTML;
      });
    }
    FacetFiltersForm.syncScopedCategoryBreadcrumb();
    FacetFiltersForm.toggleActiveFacets(false);
  }

  static usesScopedCategoryBreadcrumb() {
    const pathParts = (FacetFiltersForm.requestPath || window.location.pathname)
      .split('/')
      .filter(Boolean);
    const collectionHandle = pathParts[0] === 'collections' ? pathParts[1] : '';
    return (
      FacetFiltersForm.inPlaceCategoryRouting ||
      FacetFiltersForm.scopedCategoryBreadcrumbHandles.has(collectionHandle) ||
      document.querySelector('.breadcrumbs [data-brand-collection-reset]') !== null
    );
  }

  static syncScopedCategoryBreadcrumb() {
    const marker = '[data-scoped-category-breadcrumb]';
    document.querySelectorAll(marker).forEach(element => element.remove());

    if (!FacetFiltersForm.usesScopedCategoryBreadcrumb()) return;
    const categoryParam = FacetFiltersForm.defaultSubcategoryRefFilterParam;
    const selectedCategories = new URLSearchParams(
      FacetFiltersForm.searchParamsPrev
    ).getAll(categoryParam);
    if (selectedCategories.length !== 1) {
      FacetFiltersForm.syncInPlaceCategoryHeading('');
      return;
    }

    document
      .querySelectorAll(`.active-facets [data-filter-param="${categoryParam}"]`)
      .forEach(chip => chip.remove());
    document.querySelectorAll('.active-facets').forEach(activeFacets => {
      if (!activeFacets.querySelector('.active-facets__button')) {
        activeFacets.querySelector('.active-facets__button-remove')?.remove();
      }
    });

    const selectedRef = selectedCategories[0];
    const selectedInput = Array.from(
      document.querySelectorAll(
        `#FacetFiltersForm input[type="checkbox"][name="${categoryParam}"]`
      )
    ).find(input => input.value === selectedRef);
    const selectedLabel = selectedInput?.id
      ? document.querySelector(`label[for="${selectedInput.id.replace(/"/g, '\\"')}"]`)
      : null;
    const label =
      selectedLabel?.textContent?.trim() ||
      (FacetFiltersForm.directCategoryContext?.currentRef === selectedRef
        ? FacetFiltersForm.directCategoryContext.currentTitle
        : '');
    if (!label) return;

    FacetFiltersForm.syncInPlaceCategoryHeading(label);

    const categoryOnly = new URLSearchParams();
    categoryOnly.append(categoryParam, selectedRef);
    const resetUrl = FacetFiltersForm.getVisibleFacetUrl(categoryOnly);

    document.querySelectorAll('.breadcrumbs').forEach(nav => {
      const separator = document.createElement('i');
      separator.dataset.scopedCategoryBreadcrumb = 'true';
      separator.textContent = '/';
      const crumb = document.createElement('a');
      crumb.dataset.scopedCategoryBreadcrumb = 'true';
      crumb.dataset.filterResetCollection = '';
      crumb.href = resetUrl;
      crumb.title = label;
      crumb.textContent = label;
      nav.append(separator, crumb);
    });
  }

  static syncInPlaceCategoryHeading(categoryLabel) {
    if (!FacetFiltersForm.inPlaceCategoryRouting) return;

    document.querySelectorAll('.collection-grid-heading__title').forEach(title => {
      const baseTitle =
        title.dataset.facetBaseTitle ||
        document.querySelector('.breadcrumbs')?.dataset.facetBaseTitle ||
        document.querySelector('.breadcrumbs')?.dataset.collectionTitle;
      if (baseTitle && !title.dataset.facetBaseTitle) {
        title.dataset.facetBaseTitle = baseTitle;
      }
      title.textContent = categoryLabel || baseTitle || title.textContent;
    });
  }

  static renderAdditionalElements(html) {
    const source = html.querySelector('.mobile-filter-count');
    const target = document.querySelector('.mobile-filter-count');

    if (source && target) {
      target.innerHTML = source.innerHTML;
    }
  }

  static updateURLHash(searchParams) {
    const visibleUrl = FacetFiltersForm.getVisibleFacetUrl(searchParams);
    history.pushState(
      { searchParams, requestPath: FacetFiltersForm.requestPath },
      '',
      visibleUrl
    );
  }

  static getVisibleFacetUrl(searchParams) {
    const params = new URLSearchParams(searchParams || '');
    const categoryParam =
      FacetFiltersForm.resolveSubcategoryFilterParam(params) ||
      FacetFiltersForm.defaultSubcategoryRefFilterParam;
    const selectedCategories = params.getAll(categoryParam);
    const requestPath = FacetFiltersForm.requestPath || window.location.pathname;
    const pathParts = requestPath.split('/').filter(Boolean);
    const originHandle = pathParts[0] === 'collections' ? pathParts[1] : '';

    if (
      selectedCategories.length === 1 &&
      FacetFiltersForm.directCategoryOriginHandles.has(originHandle)
    ) {
      const selectedRef = selectedCategories[0];
      const directHandle =
        FacetFiltersForm.fallbackSubcategoryRefToHandle[selectedRef] ||
        FacetFiltersForm.subcategoryRefToHandle[selectedRef];
      if (directHandle && directHandle !== originHandle) {
        params.delete(categoryParam);
        params.delete('subcategory_from');
        params.set('subcategory_from', originHandle);
        const directQuery = FacetFiltersForm.normalizeSearchParams(params);
        return `/collections/${directHandle}${directQuery ? '?' + directQuery : ''}`;
      }
    }

    const query = FacetFiltersForm.normalizeSearchParams(params);
    return `${requestPath}${query ? '?' + query : ''}`;
  }

  static getDirectCategoryContext() {
    const url = new URL(window.location.href);
    const pathParts = url.pathname.split('/').filter(Boolean);
    const currentHandle = pathParts[0] === 'collections' ? pathParts[1] : '';
    const originHandle = FacetFiltersForm.slugify(
      url.searchParams.get('subcategory_from')
    );
    const currentRef =
      FacetFiltersForm.fallbackSubcategoryHandleToRef[currentHandle] ||
      FacetFiltersForm.subcategoryHandleToRef[currentHandle];

    if (
      !currentHandle ||
      !currentRef ||
      currentHandle === originHandle ||
      !FacetFiltersForm.directCategoryOriginHandles.has(originHandle)
    ) {
      return null;
    }

    return {
      currentHandle,
      currentRef,
      originHandle,
      requestPath: `/collections/${originHandle}`
    };
  }

  static hydrateDirectCategoryFacets() {
    const context = FacetFiltersForm.directCategoryContext;
    if (!context || FacetFiltersForm.directCategoryFacetPromise) {
      return FacetFiltersForm.directCategoryFacetPromise;
    }

    const section = FacetFiltersForm.getSections()[0];
    if (!section) return null;

    const params = new URLSearchParams(FacetFiltersForm.searchParamsPrev);
    params.delete(FacetFiltersForm.defaultSubcategoryRefFilterParam);
    const sourceUrl = `${context.requestPath}?section_id=${section.section}&${FacetFiltersForm.normalizeSearchParams(params)}`;
    FacetFiltersForm.directCategoryFacetPromise = FacetFiltersForm.fetchSectionHTML(sourceUrl, 5)
      .then(async html => {
        const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
        const categoryParam = FacetFiltersForm.defaultSubcategoryRefFilterParam;

        await Promise.all(
          ['#FacetFiltersForm', '#FacetFiltersFormMobile'].map(async formSelector => {
            const source = parsedHTML.querySelector(
              `${formSelector} collapsible-row[data-filter-param="${categoryParam}"]`
            );
            const targetForm = document.querySelector(formSelector);
            if (!source || !targetForm) return;

            const prepared = await FacetFiltersForm.prepareFacetRowForRefresh(source);
            prepared.dataset.parentCategoryFacet = 'true';
            const existing = targetForm.querySelector(
              `collapsible-row[data-filter-param="${categoryParam}"]`
            );
            if (existing) {
              existing.dataset.parentCategoryFacet = 'true';
              FacetFiltersForm.refreshFacetRowContent(existing, prepared);
              return;
            }

            const wrapper = targetForm.querySelector('.facets__wrapper');
            if (wrapper) {
              wrapper.prepend(prepared);
              return;
            }
            targetForm.insertBefore(
              prepared,
              targetForm.querySelector('collapsible-row')
            );
          })
        );

        document.documentElement.classList.remove('collection-subcategory-origin');
        FacetFiltersForm.refreshSubcategoryRefMaps();
        FacetFiltersForm.captureFacetRowTemplatesFromRoot(document, true);
        FacetFiltersForm.bindFacetAvailabilityListeners();
        FacetFiltersForm.syncScopedCategoryBreadcrumb();
      })
      .catch(() => null);

    return FacetFiltersForm.directCategoryFacetPromise;
  }

  static normalizeSearchParams(searchParams) {
    const params = new URLSearchParams(searchParams || '');
    const previewThemeId = new URL(window.location.href).searchParams.get(
      'preview_theme_id'
    );
    if (previewThemeId && !params.has('preview_theme_id')) {
      params.set('preview_theme_id', previewThemeId);
    }
    return params.toString();
  }

  static clearDependentFiltersAfterCategoryReset(nextParams) {
    const previousParams = new URLSearchParams(FacetFiltersForm.searchParamsPrev);
    const categoryParam =
      FacetFiltersForm.resolveSubcategoryFilterParam(previousParams) ||
      FacetFiltersForm.resolveSubcategoryFilterParam(nextParams) ||
      FacetFiltersForm.defaultSubcategoryRefFilterParam;
    if (
      previousParams.getAll(categoryParam).length === 0 ||
      nextParams.getAll(categoryParam).length > 0
    ) {
      return;
    }

    const sharedParams = new Set(['filter.v.availability']);
    document
      .querySelectorAll('#FacetFiltersForm collapsible-row[data-filter-param]')
      .forEach(row => {
        if (FacetFiltersForm.isSharedAcrossCategoriesFacet(row)) {
          sharedParams.add(row.dataset.filterParam);
        }
      });

    Array.from(nextParams.keys()).forEach(param => {
      if (param.startsWith('filter.') && !sharedParams.has(param)) {
        nextParams.delete(param);
      }
    });
  }

  static facetRowTemplateKey(formSelector, filterParam) {
    return `${formSelector}\u0000${filterParam || ''}`;
  }

  static captureFacetRowTemplates() {
    FacetFiltersForm.captureFacetRowTemplatesFromRoot(document);
  }

  static captureFacetRowTemplatesFromRoot(root, overwrite = false) {
    ['#FacetFiltersForm', '#FacetFiltersFormMobile'].forEach(formSelector => {
      root
        .querySelectorAll(
          `${formSelector} collapsible-row[data-filter-param]`
        )
        .forEach(row => {
          if (!row.querySelector('input[type="checkbox"][name]')) return;
          const key = FacetFiltersForm.facetRowTemplateKey(
            formSelector,
            row.dataset.filterParam
          );
          if (overwrite || !FacetFiltersForm.facetRowTemplates.has(key)) {
            FacetFiltersForm.facetRowTemplates.set(key, row.cloneNode(true));
          }
        });
    });
  }

  static getFacetRowTemplate(formSelector, filterParam) {
    return FacetFiltersForm.facetRowTemplates.get(
      FacetFiltersForm.facetRowTemplateKey(formSelector, filterParam)
    );
  }

  static syncListFacetSelectionFromSearchParams(row) {
    const filterParam = row.dataset.filterParam;
    if (!filterParam) return;

    const selectedValues = new Set(
      new URLSearchParams(FacetFiltersForm.searchParamsPrev).getAll(filterParam)
    );
    row.querySelectorAll('input[type="checkbox"][name]').forEach(input => {
      if (input.name !== filterParam) return;
      const isActive = selectedValues.has(input.value);
      input.checked = isActive;
      input.toggleAttribute('checked', isActive);
      if (isActive) input.disabled = false;
      const option = input.closest('[data-facet-option-active]');
      if (option) option.dataset.facetOptionActive = String(isActive);
    });
  }

  static bindFacetAvailabilityListeners() {
    FacetFiltersForm.updateCollectionStockCount();
    document.querySelectorAll('collapsible-row[data-inventory-aware-facet]').forEach(row => {
      const details = row.querySelector('details');
      const summary = details?.querySelector('summary');
      if (!details || !summary || details.dataset.inventoryAwareBound === 'true') return;

      details.dataset.inventoryAwareBound = 'true';
      summary.addEventListener(
        'click',
        event => {
          if (details.open || row.dataset.inventoryAwareResolved === 'true') return;

          event.preventDefault();
          event.stopImmediatePropagation();
          FacetFiltersForm.prepareFacetAvailability(row).then(resolved => {
            if (!resolved) return;
            if (!details.isConnected || details.open) return;
            if (typeof row.open === 'function') {
              row.open();
            } else {
              details.open = true;
            }
          });
        },
        { capture: true }
      );

      if (details.open) {
        FacetFiltersForm.prepareFacetAvailability(row);
      }
    });

    document.querySelectorAll(
      'collapsible-row[data-inventory-aware-facet]:not([data-inventory-aware-resolved])'
    ).forEach(row => FacetFiltersForm.prepareFacetAvailability(row));
  }

  static prepareFacetAvailability(row) {
    const existingPreparation = FacetFiltersForm.facetRowPreparations.get(row);
    if (existingPreparation) return existingPreparation;

    const preparation = FacetFiltersForm.resolveFacetAvailability(row).finally(() => {
      FacetFiltersForm.facetRowPreparations.delete(row);
    });
    FacetFiltersForm.facetRowPreparations.set(row, preparation);
    return preparation;
  }

  static async resolveFacetAvailability(row) {
    const preparationGeneration = row.dataset.inventoryAwareGeneration || '0';
    row.dataset.inventoryAwareResolving = 'true';
    const options = Array.from(row.querySelectorAll('[data-facet-option-url]'));
    const candidates = options.filter(option => {
      const input = option.querySelector('input[type="checkbox"], input[type="radio"]');
      return (
        option.dataset.facetOptionUrl &&
        option.dataset.facetOptionActive !== 'true' &&
        !input?.checked
      );
    });

    const availabilityContexts = await FacetFiltersForm.getFacetAvailabilityContexts(row);

    if ((row.dataset.inventoryAwareGeneration || '0') !== preparationGeneration) return;

    if (!availabilityContexts) {
      delete row.dataset.inventoryAwareResolved;
      delete row.dataset.inventoryAwareResolving;
      return false;
    }

    if (row.querySelector('price-checkbox-filter')) {
      FacetFiltersForm.resolvePriceRangeAvailability(
        row,
        availabilityContexts.map(context => context.availablePrices)
      );
      row.dataset.inventoryAwareResolved = 'true';
      delete row.dataset.inventoryAwareResolving;
      return true;
    }

    const availableValues = FacetFiltersForm.intersectAvailableValues(
      availabilityContexts
    );
    if (availableValues) {
      candidates.forEach(option => {
        const input = option.querySelector(
          'input[type="checkbox"], input[type="radio"]'
        );
        if (!input) return;
        if (availableValues.has(`${input.name}\u0000${input.value}`)) {
          input.disabled = false;
        } else {
          option.remove();
        }
      });
    }

    // Use the complete active category context for counts; category intersections
    // above decide visibility without adding counts from overlapping categories.
    const countSnapshot = await FacetFiltersForm.getFacetAvailabilitySnapshot(
      row, new URLSearchParams(FacetFiltersForm.searchParamsPrev)
    );
    if ((row.dataset.inventoryAwareGeneration || '0') !== preparationGeneration) return;
    if (countSnapshot) {
      FacetFiltersForm.updateFacetStockCounts(row, countSnapshot.availableCounts);
    }

    row.dataset.inventoryAwareResolved = 'true';
    delete row.dataset.inventoryAwareResolving;

    const hasOptions = !!row.querySelector('[data-facet-option-url]');
    row.hidden = !hasOptions;
    return true;
  }

  static async updateCollectionStockCount() {
    if (!FacetFiltersForm.requestPath?.startsWith('/collections/')) return;
    const params = FacetFiltersForm.searchParamsPrev;
    const generation = FacetFiltersForm.renderGeneration;
    const snapshot = await FacetFiltersForm.getFacetAvailabilitySnapshot(
      { dataset: { filterParam: 'filter.v.availability' } }, new URLSearchParams(params)
    );
    if (!snapshot || generation !== FacetFiltersForm.renderGeneration ||
        params !== FacetFiltersForm.searchParamsPrev) return;
    document.querySelectorAll('[data-stock-count-one][data-stock-count-other]').forEach(label => {
      const template = snapshot.productCount === 1
        ? label.dataset.stockCountOne : label.dataset.stockCountOther;
      label.textContent = template.replace('__COUNT__', String(snapshot.productCount));
      label.dataset.stockCountResolved = 'true';
    });
  }

  static updateFacetStockCounts(row, counts) {
    if (!counts) return;
    row.querySelectorAll('[data-facet-option-url]').forEach(option => {
      const input = option.querySelector('input[name]');
      if (!input) return;
      const count = counts.get(`${input.name}\u0000${input.value}`) || 0;
      option.querySelectorAll('[data-tooltip]').forEach(label => {
        label.dataset.tooltip = label.dataset.tooltip.replace(/\s\(\d+\)$/, ` (${count})`);
      });
      option.querySelectorAll('.count').forEach(counter => {
        counter.textContent = counter.textContent.trim().startsWith('(')
          ? `(${count})` : String(count);
      });
    });
  }

  static collectFacetStockCounts(documents) {
    const counts = new Map();
    documents.forEach(document => {
      document.querySelectorAll('#FacetAvailabilityProbe [data-facet-stock-count]')
        .forEach(option => {
          const key = `${option.dataset.facetOptionName}\u0000${option.dataset.facetOptionValue}`;
          const count = Number(option.dataset.facetStockCount);
          if (!Number.isFinite(count) || count < 0) return;
          // Each probe page counts its own in-stock products once per option.
          counts.set(key, (counts.get(key) || 0) + count);
        });
    });
    return counts;
  }

  static intersectAvailableValues(availabilityContexts) {
    const [firstContext, ...remainingContexts] = availabilityContexts;
    if (!firstContext) return new Set();

    return new Set(
      Array.from(firstContext.availableValues).filter(value =>
        remainingContexts.every(context => context.availableValues.has(value))
      )
    );
  }

  static resolvePriceRangeAvailability(row, availablePriceContexts) {
    if (!availablePriceContexts?.length) return;

    row.querySelectorAll('.price-checkbox-filter__input').forEach(input => {
      const minimum = Number(input.dataset.priceMin || 0) * 100;
      const maximumValue = input.dataset.priceMax;
      const maximum = maximumValue === '' ? null : Number(maximumValue) * 100;
      const hasMatchingPrice = availablePriceContexts.every(prices =>
        prices.some(price => price >= minimum && (maximum === null || price <= maximum))
      );
      const option = input.closest('li');
      if (!option) return;
      option.hidden = !input.checked && !hasMatchingPrice;
      input.disabled = !input.checked && !hasMatchingPrice;
    });

    row.hidden = !row.querySelector(
      '.price-checkbox-filter__options li:not([hidden]), .price-checkbox-filter__input:checked'
    );
  }

  static getFacetAvailabilityContexts(row) {
    const filterParam = row.dataset.filterParam;
    if (!filterParam) return Promise.resolve(null);
    const activeParams = new URLSearchParams(FacetFiltersForm.searchParamsPrev);
    const categoryParam =
      FacetFiltersForm.resolveSubcategoryFilterParam(activeParams) ||
      FacetFiltersForm.defaultSubcategoryRefFilterParam;
    const selectedCategories = activeParams.getAll(categoryParam);

    if (
      filterParam === categoryParam ||
      selectedCategories.length < 2 ||
      FacetFiltersForm.isSharedAcrossCategoriesFacet(row)
    ) {
      return FacetFiltersForm.getFacetAvailabilitySnapshot(row, activeParams).then(
        availability => (availability ? [availability] : null)
      );
    }

    return Promise.all(
      selectedCategories.map(category => {
        const categoryParams = new URLSearchParams(activeParams);
        categoryParams.delete(categoryParam);
        categoryParams.append(categoryParam, category);
        return FacetFiltersForm.getFacetAvailabilitySnapshot(row, categoryParams);
      })
    ).then(contexts => (contexts.every(Boolean) ? contexts : null));
  }

  static isSharedAcrossCategoriesFacet(row) {
    if (row.dataset.filterParam === 'filter.v.availability') return true;

    const label = FacetFiltersForm.slugify(row.querySelector('summary')?.textContent);
    return ['brand', 'size', 'gender', 'color', 'price'].includes(label);
  }

  static getFacetAvailabilitySnapshot(row, activeParams) {
    const filterParam = row.dataset.filterParam;
    if (!filterParam) return Promise.resolve(null);
    const url = new URL(
      FacetFiltersForm.requestPath || window.location.pathname,
      window.location.origin
    );
    url.search = activeParams.toString();
    url.searchParams.delete('page');
    url.searchParams.delete('section_id');
    url.searchParams.delete(filterParam);
    url.searchParams.delete(`${filterParam}.gte`);
    url.searchParams.delete(`${filterParam}.lte`);
    url.searchParams.set('facet_stock_counts', '3');
    url.searchParams.set('section_id', 'facet-availability-probe');
    const snapshotUrl = `${url.pathname}?${FacetFiltersForm.normalizeSearchParams(
      url.searchParams
    )}`;
    const cached = FacetFiltersForm.facetAvailabilitySnapshots.get(snapshotUrl);
    if (cached) return cached;

    const snapshot = FacetFiltersForm.fetchFacetAvailabilityPages(snapshotUrl)
      .catch(() => {
        FacetFiltersForm.facetAvailabilitySnapshots.delete(snapshotUrl);
        return null;
      });
    FacetFiltersForm.facetAvailabilitySnapshots.set(snapshotUrl, snapshot);
    return snapshot;
  }

  static async fetchFacetAvailabilityPages(snapshotUrl) {
    const firstHTML = await FacetFiltersForm.fetchSectionHTML(snapshotUrl, 5);
    const firstPage = new DOMParser().parseFromString(firstHTML, 'text/html');
    const probe = firstPage.getElementById('FacetAvailabilityProbe');
    if (!probe) throw new Error('Unable to read collection facet availability');

    const pages = Math.max(1, Number(probe.dataset.pages) || 1);
    const documents = [firstPage];
    if (pages > 1) {
      const remainingPages = await Promise.all(
        Array.from({ length: pages - 1 }, (_, index) => {
          const pageUrl = new URL(snapshotUrl, window.location.origin);
          pageUrl.searchParams.set('page', String(index + 2));
          return FacetFiltersForm.fetchSectionHTML(
            `${pageUrl.pathname}?${FacetFiltersForm.normalizeSearchParams(
              pageUrl.searchParams
            )}`,
            5
          ).then(html => new DOMParser().parseFromString(html, 'text/html'));
        })
      );
      documents.push(...remainingPages);
    }

    const availableCounts = FacetFiltersForm.collectFacetStockCounts(documents);
    const availableValues = new Set(
      documents.flatMap(document =>
        Array.from(
          document.querySelectorAll(
            '#FacetAvailabilityProbe [data-facet-option-name][data-facet-option-value]'
          )
        ).map(
          option => `${option.dataset.facetOptionName}\u0000${option.dataset.facetOptionValue}`
        )
      )
    );
    availableCounts.forEach((count, key) => {
      if (count > 0) availableValues.add(key);
      else availableValues.delete(key);
    });

    return {
      availableValues,
      availableCounts,
      productCount: documents.reduce((total, document) => total + Number(
        document.getElementById('FacetAvailabilityProbe')?.dataset.stockProducts || 0
      ), 0),
      availablePrices: documents.flatMap(document =>
        Array.from(
          document.querySelectorAll(
            '#FacetAvailabilityProbe [data-facet-available-price]'
          )
        )
          .map(option => Number(option.dataset.facetAvailablePrice))
          .filter(Number.isFinite)
      )
    };
  }

  static resolveSubcategoryFilterParam(params) {
    if (FacetFiltersForm.subcategoryRefFilterParam) {
      return FacetFiltersForm.subcategoryRefFilterParam;
    }
    if (!params) return '';
    for (const key of params.keys()) {
      if (key.includes('custom.subcategory_ref')) {
        return key;
      }
    }
    return '';
  }

  static refreshSubcategoryRefMaps() {
    const checkboxSelector = [
      '#FacetFiltersForm input[type="checkbox"][name*="custom.subcategory_ref"]',
      '#FacetFiltersFormMobile input[type="checkbox"][name*="custom.subcategory_ref"]'
    ].join(', ');
    const subcategoryInputs = Array.from(document.querySelectorAll(checkboxSelector));

    const handleToRef = {};
    const refToHandle = {};
    let filterParam = FacetFiltersForm.subcategoryRefFilterParam || '';

    subcategoryInputs.forEach((input) => {
      if (!filterParam && input.name) {
        filterParam = input.name;
      }

      const refId = input.value;
      if (!refId) return;

      let labelText = '';
      if (input.id) {
        const label = document.querySelector(`label[for="${input.id.replace(/"/g, '\\"')}"]`);
        if (label) {
          labelText = label.textContent || '';
        }
      }

      if (!labelText) {
        const textNode = input.closest('.facet-checkbox')?.querySelector('.facet-checkbox-text');
        if (textNode) {
          labelText = textNode.textContent || '';
        }
      }

      const handle = FacetFiltersForm.slugify(labelText);
      if (!handle) return;

      handleToRef[handle] = refId;
      refToHandle[refId] = handle;
    });

    if (!filterParam) return;

    FacetFiltersForm.subcategoryRefFilterParam = filterParam;
    FacetFiltersForm.subcategoryHandleToRef = handleToRef;
    FacetFiltersForm.subcategoryRefToHandle = refToHandle;
  }

  static slugify(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/&/g, ' and ')
      .replace(/['"]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  static getSections() {
    return [{
      section: document.getElementById('product-grid').dataset.id
    }];
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const formData = new FormData(event.target.closest('form'));
    const searchParams = new URLSearchParams(formData);
    const directCategoryContext = FacetFiltersForm.directCategoryContext;
    const categoryParam = FacetFiltersForm.defaultSubcategoryRefFilterParam;
    const changedCategoryControl =
      event.target?.matches?.(`input[type="checkbox"][name="${categoryParam}"]`);
    if (
      directCategoryContext &&
      !changedCategoryControl &&
      !searchParams.has(categoryParam)
    ) {
      searchParams.append(categoryParam, directCategoryContext.currentRef);
    }

    if (Number(searchParams.get('filter.v.price.gte')) === 0) {
      searchParams.delete('filter.v.price.gte');
    }

    const priceFilter = event.target
      .closest('form')
      ?.querySelector('price-checkbox-filter');
    if (priceFilter) {
      if (
        Number(searchParams.get('filter.v.price.lte')) ===
        Number(priceFilter.dataset.rangeMax)
      ) {
        searchParams.delete('filter.v.price.lte');
      }
    }

    FacetFiltersForm.clearDependentFiltersAfterCategoryReset(searchParams);

    FacetFiltersForm.renderPage(searchParams.toString(), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const currentParams = new URLSearchParams(FacetFiltersForm.searchParamsPrev);
    const categoryParam = FacetFiltersForm.defaultSubcategoryRefFilterParam;
    const selectedCategories = currentParams.getAll(categoryParam);

    // A clean direct category URL represents the category in its breadcrumb,
    // not as a removable chip. Clearing the remaining chips should therefore
    // keep that category selected even if a stale server-rendered Clear all
    // link still points at the parent collection during an AJAX refresh.
    if (
      event.currentTarget.classList?.contains('active-facets__button-remove') &&
      FacetFiltersForm.directCategoryContext &&
      selectedCategories.length === 1
    ) {
      const categoryOnly = new URLSearchParams();
      categoryOnly.append(categoryParam, selectedCategories[0]);
      window.location.href = FacetFiltersForm.getVisibleFacetUrl(categoryOnly);
      return;
    }

    const targetUrl = new URL(event.currentTarget.href, window.location.origin);

    // If filter action points to a different route (for example, switching
    // from a subcategory collection back to /collections/all), perform full
    // navigation so path changes are applied.
    if (targetUrl.pathname !== FacetFiltersForm.requestPath) {
      window.location.href = targetUrl.toString();
      return;
    }

    const query = targetUrl.search.startsWith('?')
      ? targetUrl.search.slice(1)
      : '';
    const searchParams = new URLSearchParams(query);
    FacetFiltersForm.clearDependentFiltersAfterCategoryReset(searchParams);
    FacetFiltersForm.renderPage(
      FacetFiltersForm.normalizeSearchParams(searchParams.toString())
    );
  }

}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = '';
FacetFiltersForm.searchParamsPrev = '';
FacetFiltersForm.facetAvailabilitySnapshots = new Map();
FacetFiltersForm.facetRowPreparations = new WeakMap();
FacetFiltersForm.facetRowTemplates = new Map();
FacetFiltersForm.subcategoryRefFilterParam = '';
FacetFiltersForm.subcategoryHandleToRef = {};
FacetFiltersForm.subcategoryRefToHandle = {};
FacetFiltersForm.defaultSubcategoryRefFilterParam = 'filter.p.m.custom.subcategory_ref';
FacetFiltersForm.requestPath = window.location.pathname;
FacetFiltersForm.categoryOpenStorageKey = 'transport:category-facet-open-once';
FacetFiltersForm.collectionResetLinksBound = false;
FacetFiltersForm.keepCategoryFacetOpen = false;
FacetFiltersForm.inPlaceCategoryRouting = false;
FacetFiltersForm.directCategoryOriginHandles = new Set([
  'accessories',
  'clothing',
  'shoes',
  'skate'
]);
FacetFiltersForm.scopedCategoryBreadcrumbHandles = new Set([
  'kids-clothing',
  'mens-clothing',
  'womens-clothing'
]);
FacetFiltersForm.fallbackSubcategoryHandleToRef = {
  'bags-luggage': 'gid://shopify/Metaobject/127371018517',
  'complete-skateboards': 'gid://shopify/Metaobject/127371280661',
  'hoodies-sweatshirts': 'gid://shopify/Metaobject/127371411733',
  jackets: 'gid://shopify/Metaobject/127371444501',
  'lubricant-cleaner': 'gid://shopify/Metaobject/127371542805',
  pants: 'gid://shopify/Metaobject/127371575573',
  'protective-gear': 'gid://shopify/Metaobject/127371641109',
  sandals: 'gid://shopify/Metaobject/127371215125',
  shirts: 'gid://shopify/Metaobject/127450579221',
  shorts: 'gid://shopify/Metaobject/127371673877',
  'skate-shoes': 'gid://shopify/Metaobject/127371706645',
  'skateboard-decks': 'gid://shopify/Metaobject/127371837717',
  'skateboard-hardware': 'gid://shopify/Metaobject/127371870485',
  'skateboard-tools': 'gid://shopify/Metaobject/127371936021',
  'skateboard-trucks': 'gid://shopify/Metaobject/127371968789',
  'skateboard-wheels': 'gid://shopify/Metaobject/127372001557',
  sneakers: 'gid://shopify/Metaobject/127446286613',
  sunglasses: 'gid://shopify/Metaobject/127372067093',
  sweaters: 'gid://shopify/Metaobject/127372132629',
  't-shirts': 'gid://shopify/Metaobject/127372165397'
};
FacetFiltersForm.fallbackSubcategoryRefToHandle = Object.fromEntries(
  Object.entries(FacetFiltersForm.fallbackSubcategoryHandleToRef).map(([handle, ref]) => [ref, handle])
);
FacetFiltersForm.initialized = false;
FacetFiltersForm.renderGeneration = 0;
FacetFiltersForm.scrollAnchorStyles = null;
FacetFiltersForm.directCategoryContext = null;
FacetFiltersForm.directCategoryFacetPromise = null;

customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

/* ---------------- Facet Remove ---------------- */
class FacetRemove extends HTMLElement {
  constructor() {
    super();
    this.onClick = this.onClick.bind(this);
  }

  connectedCallback() {
    this.addEventListener('click', this.onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
  }

  onClick(event) {
    const link = event.target.closest('a');
    if (!link || !this.contains(link)) return;
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    if (form && typeof form.onActiveFilterClick === 'function') {
      form.onActiveFilterClick({
        preventDefault: () => {},
        currentTarget: link
      });
      return;
    }

    // Fallback to native navigation when no facet form context is available.
    window.location.href = link.href;
  }
}
customElements.define('facet-remove', FacetRemove);

/* ---------------- Price Checkboxes ---------------- */
class PriceCheckboxFilter extends HTMLElement {
  connectedCallback() {
    this.addEventListener('input', (event) => {
      const selected = event.target.closest('.price-checkbox-filter__input');
      if (!selected || !this.contains(selected)) return;

      const minInput = this.querySelector('.price-checkbox-filter__min');
      const maxInput = this.querySelector('.price-checkbox-filter__max');
      if (!minInput || !maxInput) return;

      this.querySelectorAll('.price-checkbox-filter__input').forEach(input => {
        if (input !== selected) input.checked = false;
      });

      const minValue = selected.checked ? selected.dataset.priceMin || '' : '';
      const maxValue = selected.checked ? selected.dataset.priceMax || '' : '';
      minInput.value = minValue;
      minInput.disabled = minValue === '' || Number(minValue) === 0;
      maxInput.value = maxValue;
      maxInput.disabled = maxValue === '';
    });
  }
}
customElements.define('price-checkbox-filter', PriceCheckboxFilter);

window.addEventListener('load', () => {
  new FacetsToggle();
});
